import type { BlockNode, DocumentLayout, Rect } from './layout-types'

/**
 * 将逻辑布局投影到显示坐标，保留源节点、路径和字符偏移。
 * 不修改引擎缓存或文档数据，缩放不会改变分页、字号和导出结果。
 */
export function scaleLayout(layout: DocumentLayout, scale: number): DocumentLayout {
  if (scale === 1) return layout
  const rect = (value: Rect): Rect => ({
    x: value.x * scale, y: value.y * scale,
    width: value.width * scale, height: value.height * scale
  })
  const optional = (value: number | undefined) => value === undefined ? undefined : value * scale
  const block = (value: BlockNode): BlockNode => {
    if (value.kind === 'paragraph') {
      return {
        ...value, rect: rect(value.rect),
        bulletSize: optional(value.bulletSize),
        bulletWidth: optional(value.bulletWidth),
        bulletX: optional(value.bulletX),
        surroundImage: value.surroundImage
          ? { ...value.surroundImage, rect: rect(value.surroundImage.rect) } : undefined,
        lines: value.lines.map(line => ({
          ...line, ...rect(line), baseline: line.baseline * scale,
          inlines: line.inlines.map(inline => ({
            ...inline, ...rect(inline),
            size: inline.size * scale, baseline: inline.baseline * scale,
            letterSpacing: optional(inline.letterSpacing),
            glyphAdvance: optional(inline.glyphAdvance),
            glyphOffset: optional(inline.glyphOffset)
          }))
        }))
      }
    }
    if (value.kind === 'table') {
      return {
        ...value, rect: rect(value.rect),
        colWidths: value.colWidths.map(width => width * scale),
        rows: value.rows.map(row => ({
          ...row, rect: rect(row.rect),
          cells: row.cells.map(cell => ({
            ...cell, rect: rect(cell.rect),
            contentPaddingTop: cell.contentPaddingTop * scale,
            contentPaddingLeft: cell.contentPaddingLeft * scale,
            verticalOffset: cell.verticalOffset * scale,
            content: cell.content.map(block)
          }))
        }))
      }
    }
    return { ...value, rect: rect(value.rect) }
  }
  return {
    ...layout,
    pageWidth: layout.pageWidth * scale,
    totalHeight: layout.totalHeight * scale,
    pages: layout.pages.map(page => ({
      ...page, rect: rect(page.rect), contentRect: rect(page.contentRect),
      headerRect: page.headerRect && rect(page.headerRect),
      footerRect: page.footerRect && rect(page.footerRect),
      blocks: page.blocks.map(block),
      headerBlocks: page.headerBlocks?.map(block),
      footerBlocks: page.footerBlocks?.map(block)
    }))
  }
}
